import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../utils/i18n';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);

  // Supported languages with their names in native languages
  const supportedLanguages = {
    en: 'English',
    hi: 'हिन्दी',
    ta: 'தமிழ்',
    te: 'తెలుగు',
    kn: 'ಕನ್ನಡ',
    ml: 'മലയാളം',
    bn: 'বাংলা',
    mr: 'मराठी',
    gu: 'ગુજરાતી',
    pa: 'ਪੰਜਾਬੀ',
    fr: 'Français',
    es: 'Español',
    de: 'Deutsch'
  };

  // Load saved language preference from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
    // Also update i18n language
    i18n.changeLanguage(savedLanguage);
  }, []);

  // Save language preference to localStorage and trigger update
  const changeLanguage = (languageCode) => {
    console.log('Changing language to:', languageCode);
    setCurrentLanguage(languageCode);
    localStorage.setItem('language', languageCode);
    // Also update i18n language
    i18n.changeLanguage(languageCode);
    // Force a re-render by updating state
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: languageCode }));
  };

  // Translate text using LibreTranslate API
  const translateText = async (text, targetLanguage) => {
    if (!text || targetLanguage === 'en') return text;
    
    try {
      setLoading(true);
      const response = await fetch('https://libretranslate.de/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          q: text,
          source: 'en',
          target: targetLanguage,
          format: 'text'
        })
      });
      
      const data = await response.json();
      if (data.translatedText) {
        return data.translatedText;
      }
      return text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    } finally {
      setLoading(false);
    }
  };

  // Translate product data
  const translateProduct = async (product, targetLanguage) => {
    if (targetLanguage === 'en') return product;
    
    try {
      const translatedProduct = { ...product };
      
      // Translate title
      if (product.title) {
        translatedProduct.title = await translateText(product.title, targetLanguage);
      }
      
      // Translate description
      if (product.description) {
        translatedProduct.description = await translateText(product.description, targetLanguage);
      }
      
      // Translate category
      if (product.category) {
        translatedProduct.category = await translateText(product.category, targetLanguage);
      }
      
      // Translate brand
      if (product.brand) {
        translatedProduct.brand = await translateText(product.brand, targetLanguage);
      }
      
      return translatedProduct;
    } catch (error) {
      console.error('Product translation error:', error);
      return product;
    }
  };

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      supportedLanguages,
      changeLanguage,
      translateText,
      translateProduct,
      loading
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};